import { Check, Copy01 } from '@untitledui/icons';
import { useCallback } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';

interface CopyEntityIdButtonProps {
  /** The raw entity UUID placed on the clipboard. */
  entityId: string;
}

// Polite live region announcing a successful copy to screen-reader users.
// Mirrors CopyAnnouncement in CrmWebhookSecretModal.tsx — the established
// pattern for this in the codebase.
function CopyAnnouncement({ copied }: { copied: boolean }) {
  return (
    <span className='sr-only' aria-live='polite' aria-atomic='true'>
      {copied ? 'Entity ID copied' : ''}
    </span>
  );
}

/**
 * Internal-only affordance for copying a record's entity ID out of the
 * slideout header.
 *
 * Gated on the same `useIsInternalUser` check that decides whether the Debug
 * page is reachable at all (see `SidebarUserFooter`), so the two surfaces
 * appear and disappear together for a given user. The hook is React
 * Query-backed and shared, so mounting this in the header adds no request.
 *
 * Renders nothing — not a disabled button — for everyone else: an entity ID is
 * debugging plumbing, and a customer has no use for a control that reveals it.
 */
export function CopyEntityIdButton({ entityId }: CopyEntityIdButtonProps) {
  const isInternalUser = useIsInternalUser();
  const { copied, copy } = useClipboard();

  const handleCopy = useCallback(() => {
    // Pass entityId as the `id` so `copied` can be compared against the
    // specific entity that was copied, not just a generic truthy flag.
    void copy(entityId, entityId);
  }, [copy, entityId]);

  // `useIsInternalUser` returns null while the access check is in flight —
  // only an explicit `true` mounts the button, so a slow check never flashes
  // the control at a non-internal user.
  if (isInternalUser !== true || !entityId) {
    return null;
  }

  // Equality (not truthiness) against the current entityId: if entityId
  // changes before the hook's internal timeout resets `copied`, the stale
  // value no longer matches, so the button correctly falls back to the
  // un-copied state instead of showing a false confirmation for the wrong
  // record.
  const isCopied = copied === entityId;

  return (
    <>
      <Tooltip
        title={isCopied ? 'Copied' : 'Copy entity ID'}
        placement='bottom'
      >
        <TooltipTrigger
          onPress={handleCopy}
          className='p-1 hover:bg-[var(--color-bg-tertiary)] rounded-md transition-colors cursor-pointer'
          aria-label={isCopied ? 'Entity ID copied' : 'Copy entity ID'}
        >
          {isCopied ? (
            <Check className='w-3.5 h-3.5 text-success-600' />
          ) : (
            <Copy01 className='w-3.5 h-3.5 text-fg-quaternary' />
          )}
        </TooltipTrigger>
      </Tooltip>
      <CopyAnnouncement copied={isCopied} />
    </>
  );
}
